/* =============================================================================
   VIEWS / ADMIN / MENU-EDITOR — Speisekarten-Verwaltung (v0.19.0)
   =============================================================================
   Reicher Editor für die digitale Ankreuz-Speisekarte. Persistenz über
   App.storage.menu (Supabase Tabelle `menu_items`).

   Pro Item lassen sich pflegen:
     - Name, Kategorie, Subkategorie, Preis, aktiv-Flag, Reihenfolge
     - Item-Type (main/side/drink/extra/sauce/option)
     - Beschreibung, Einheit (z.B. "0,4l"), Diät-Tags
     - Zutaten (Komma-getrennt, einzeln vom Gast streichbar)
     - Meta-JSON (Power-User: Brötchen/Patty-Konfiguration)

   Strategie: Liste nach Kategorien gruppiert, jede Zeile zeigt die
   Basis-Felder inline. „Details öffnen" expandiert eine Sub-Box mit
   den selteneren Feldern. Damit bleibt die Liste übersichtlich und
   90% aller Pflege-Aktionen sind 1-Klick.
============================================================================ */

import { htmlEscape, uid, showToast, requireEditPermission } from '../../utils/helpers.js';
import { buildCategoryTree } from '../../core/menu-categories.js';

const CATEGORY_ORDER = [
  'Burger', 'Bowls', 'Salate', 'Beilagen', 'Soßen',
  'Getränke', 'Kinder', 'Extras', 'Optionen'
];

// S153 — erst-klassige Rubriken (flache Liste, on-demand geladen, flag-gated).
let _categories = [];
function categoriesFlagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
async function ensureCategoriesLoaded() {
  if (!categoriesFlagOn()) return;
  try {
    _categories = await window.App.storage.menuCategories.list();
  } catch (e) { console.warn('[menu] categories load:', e); _categories = []; }
}

const ITEM_TYPES = [
  { v: 'main',   label: 'Hauptgericht' },
  { v: 'side',   label: 'Beilage' },
  { v: 'drink',  label: 'Getränk' },
  { v: 'extra',  label: 'Extra (Topping)' },
  { v: 'sauce',  label: 'Soße' },
  { v: 'option', label: 'Option (Brötchen/Patty/Dressing)' }
];

const DIET_TAGS = [
  'vegan', 'vegetarisch', 'fisch', 'rindfleisch', 'hähnchen',
  'scharf', 'halal', 'glutenfrei'
];

// In-Memory: welche Detail-Boxen sind offen (per item.id)
const _expandedIds = new Set();

// S217 Cut 1 — welche Rubrik-Gruppen sind aufgeklappt (per Kategorie-Name).
// Modul-State (nicht DOM), damit ein Realtime-/Re-Render den Collapse-Zustand
// nicht zurücksetzt (gg-non-destructive-render). Default: alle eingeklappt.
const _expandedCats = new Set();

// S188-U — Speisekarte-Hub (System → Speisekarte). Zwei Unter-Ansichten:
//   'studio' — das Rubriken-/Karten-Studio (menu-categories-admin, zog von „Ware")
//   'editor' — der klassische Gericht-/Menü-Editor (diese Datei)
// Default = Studio. Modul-State überlebt Tab-Wechsel (letzte Ansicht bleibt aktiv),
// die Mount-Flags werden bei jedem Hub-Neubau (renderMenuEditor) zurückgesetzt,
// damit ein Toggle innerhalb einer Tab-Sitzung NICHT neu rendert (non-destruktiv).
let _menuHubView = 'studio';
let _studioMounted = false;
let _editorMounted = false;
let _optionsetsMounted = false;   // S188-2 — dritte Hub-Ansicht „Optionssätze"

const _HUB_VIEWS = ['studio', 'optionsets', 'editor'];

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { menu: [] };
}

// -----------------------------------------------------------------------------
// Top-Level
// -----------------------------------------------------------------------------
/**
 * S188-U — Einstieg „System → Speisekarte". Ist der Studio-Flag (ingredientsDb)
 * aus, verhält sich der Tab exakt wie früher: nur der klassische Menü-Editor.
 * Ist er an, wird der Tab zum Hub mit Unter-Navigation (Studio | Gericht-Editor),
 * Default-Ansicht Studio. Der Hub-Rahmen wird bei jedem Tab-Eintritt neu gebaut
 * (Tab-Level-Render, erwartet); die aktive Unter-Ansicht bleibt erhalten.
 */
export function renderMenuEditor() {
  const tab = document.getElementById('tab-menu');
  if (!tab) return;
  // Flag aus → klassischer Menü-Editor direkt in #tab-menu (Alt-Verhalten 1:1).
  if (!categoriesFlagOn()) {
    tab.innerHTML = `<div id="menu-hub-editor"></div>`;
    _renderMenuEditorInner();
    return;
  }
  // Flag an → Speisekarte-Hub mit Unter-Navigation. Der Studio-Container behält
  // seine ID #tab-categories (menu-categories-admin rendert unverändert dorthin),
  // liegt jetzt aber innerhalb des Hubs statt als eigener Tab.
  tab.innerHTML = `
    <div class="card menu-hub-head">
      <div>
        <h2 style="margin:0;">Speisekarte</h2>
        <p class="hint" style="margin:4px 0 0;">Alles Karten-Gestalterische an einem Ort:
        links die Karte gestalten (Rubriken · Reihenfolge · Gerichte zuordnen), im
        Gericht-Editor die einzelnen Gerichte pflegen.</p>
      </div>
      <nav class="menu-hub-nav" role="tablist" aria-label="Speisekarte-Bereiche">
        <button type="button" data-menuhub="studio" role="tab"
                onclick="menuHubShow('studio')">Karte gestalten</button>
        <button type="button" data-menuhub="optionsets" role="tab"
                onclick="menuHubShow('optionsets')">Optionssätze</button>
        <button type="button" data-menuhub="editor" role="tab"
                onclick="menuHubShow('editor')">Gericht-Editor</button>
      </nav>
    </div>
    <div id="menu-hub-studio" class="menu-hub-pane"><div id="tab-categories"></div></div>
    <div id="menu-hub-optionsets" class="menu-hub-pane" style="display:none;"><div id="tab-option-groups"></div></div>
    <div id="menu-hub-editor" class="menu-hub-pane" style="display:none;"></div>
  `;
  // Mount-Flags zurücksetzen (frischer Hub-Rahmen), dann aktive Ansicht zeigen.
  _studioMounted = false;
  _editorMounted = false;
  _optionsetsMounted = false;
  menuHubShow(_menuHubView);
}

/** Rendert den klassischen Gericht-/Menü-Editor in seinen Host (#menu-hub-editor). */
function _renderMenuEditorInner() {
  const host = document.getElementById('menu-hub-editor');
  if (!host) return;
  host.innerHTML = `
    <div class="card">
      <div style="display:flex; justify-content:space-between; align-items:flex-start; gap: 12px; margin-bottom: 14px; flex-wrap: wrap;">
        <div>
          <h2 style="margin: 0;">Gericht-Editor</h2>
          <p style="color: var(--ink-soft); font-size: .9rem; margin: 4px 0 0;">
            Diese Gerichte erscheinen bei der Vorbestellung im Reservierungsformular
            als digitale Ankreuz-Karte. Zutaten kann der Gast einzeln durchstreichen,
            das Patty kann er gegen eine Alternative tauschen.
          </p>
        </div>
        <button class="btn btn-primary btn-sm" onclick="addMenuItem()">+ Gericht hinzufügen</button>
      </div>
      <div id="menu-list" style="margin-top: 10px;"></div>
    </div>
    <!-- S58 Block II — Restore-Sektion für soft-gelöschte Menu-Items -->
    <details class="card" style="margin-top:14px;">
      <summary style="cursor:pointer; font-weight:600; padding:4px 0;">
        🗑️ Gelöschte Gerichte (wiederherstellbar)
      </summary>
      <div id="menu-deleted-list" style="margin-top:10px;"></div>
    </details>
  `;
  renderMenuList();
  // S153 — Rubriken (für Dropdown) flag-gated nachladen, dann offene Details neu
  // rendern, damit das Kategorie-Dropdown gefüllt ist.
  if (categoriesFlagOn()) {
    ensureCategoriesLoaded().then(() => renderMenuList()).catch(() => {});
  }
  // Restore-View asynchron nachladen (DB-Roundtrip, blockiert Haupt-Liste nicht).
  renderDeletedMenuList().catch(e => console.warn('[menu] renderDeletedMenuList:', e));
}

/** S188-U — Hub-Unteransicht vorwählen OHNE DOM-Zugriff (für switchTab-Alias,
 *  bevor der Hub gebaut ist). renderMenuEditor liest _menuHubView. */
export function menuHubSetView(view) {
  _menuHubView = _HUB_VIEWS.includes(view) ? view : 'studio';
}

/** S188-U/S188-2 — zwischen Studio | Optionssätze | Gericht-Editor umschalten.
 *  Rein Sichtbarkeit + Lazy-Mount (jede Ansicht wird pro Hub-Sitzung genau einmal
 *  gerendert → ein Toggle wirft laufende Edits NICHT weg, kein Full-Re-Render). */
export function menuHubShow(view) {
  _menuHubView = _HUB_VIEWS.includes(view) ? view : 'studio';
  const panes = {
    studio: document.getElementById('menu-hub-studio'),
    optionsets: document.getElementById('menu-hub-optionsets'),
    editor: document.getElementById('menu-hub-editor')
  };
  if (!panes.studio || !panes.optionsets || !panes.editor) return;
  _HUB_VIEWS.forEach(k => { panes[k].style.display = (k === _menuHubView) ? '' : 'none'; });
  const nav = document.querySelector('#tab-menu .menu-hub-nav');
  if (nav) nav.querySelectorAll('[data-menuhub]').forEach(b => {
    const on = b.dataset.menuhub === _menuHubView;
    b.classList.toggle('active', on);
    b.setAttribute('aria-selected', on ? 'true' : 'false');
  });
  if (_menuHubView === 'studio') {
    if (!_studioMounted) {
      _studioMounted = true;
      if (typeof window.renderMenuCategoriesAdmin === 'function') window.renderMenuCategoriesAdmin();
    }
  } else if (_menuHubView === 'optionsets') {
    if (!_optionsetsMounted) {
      _optionsetsMounted = true;
      if (typeof window.renderOptionGroupsAdmin === 'function') window.renderOptionGroupsAdmin();
    }
  } else if (!_editorMounted) {
    _editorMounted = true;
    _renderMenuEditorInner();
  }
}

export function renderMenuList() {
  const state = getState();
  const wrap = document.getElementById('menu-list');
  if (!wrap) return;

  const menu = (state.menu || []).slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const grouped = {};
  menu.forEach(m => {
    const cat = m.category || 'Sonstiges';
    (grouped[cat] = grouped[cat] || []).push(m);
  });

  const cats = [
    ...CATEGORY_ORDER.filter(c => grouped[c]),
    ...Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c))
  ];

  // S217 Cut 1 — Rubrik-Gruppen standardmäßig eingeklappt (nur Kopfzeile mit
  // Name + Zähler), per Klick aufklappbar. Collapse-State als Modul-State
  // (_expandedCats), kein natives <details> mehr (DOM-State überlebt Re-Render
  // nicht). Kategorie-Name über data-Attribut (htmlEscape-sicher) statt als
  // JS-String-Argument → kein Quoting-Bruch bei Sonderzeichen.
  wrap.innerHTML = cats.map(cat => {
    const open = _expandedCats.has(cat);
    return `
    <div class="menu-cat-block ${open ? 'open' : ''}">
      <div class="menu-cat-summary" role="button" tabindex="0"
           data-cat="${htmlEscape(cat)}" onclick="toggleMenuCat(this)">
        <strong>${htmlEscape(cat)}</strong>
        <span class="menu-cat-count">${grouped[cat].length}</span>
      </div>
      ${open ? `<div class="menu-cat-body">${grouped[cat].map(renderMenuRow).join('')}</div>` : ''}
    </div>`;
  }).join('');
}

/** S217 Cut 1 — Rubrik-Gruppe im Gericht-Editor auf-/zuklappen. Liest den
 *  Kategorie-Namen aus dem data-Attribut des geklickten Kopfes. */
export function toggleMenuCat(el) {
  const cat = el && el.dataset ? el.dataset.cat : null;
  if (!cat) return;
  if (_expandedCats.has(cat)) _expandedCats.delete(cat);
  else _expandedCats.add(cat);
  renderMenuList();
}

function renderMenuRow(m) {
  const isExpanded = _expandedIds.has(m.id);
  return `
    <div class="menu-item-row ${isExpanded ? 'expanded' : ''}" data-item="${htmlEscape(m.id)}">
      <div class="menu-item-base">
        <input type="text" value="${htmlEscape(m.name || '')}"
               class="me-name"
               placeholder="Name"
               onchange="updateMenu('${m.id}','name', this.value)">
        <input type="text" value="${htmlEscape(m.subcategory || '')}"
               class="me-sub"
               placeholder="Unterkategorie"
               onchange="updateMenu('${m.id}','subcategory', this.value)">
        <input type="number" step="0.10" value="${m.price ?? 0}"
               class="me-price"
               onchange="updateMenu('${m.id}','price', parseFloat(this.value))">
        <input type="number" step="10" value="${m.displayOrder ?? 0}"
               class="me-order"
               title="Reihenfolge"
               onchange="updateMenu('${m.id}','displayOrder', parseInt(this.value, 10))">
        <label class="checkbox" style="font-size:.85rem; white-space: nowrap;">
          <input type="checkbox" ${m.available ? 'checked' : ''}
                 onchange="updateMenu('${m.id}','available', this.checked)">
          aktiv
        </label>
        <button class="btn btn-ghost btn-sm" onclick="editMenuItem('${m.id}')">
          ${categoriesFlagOn() ? '✎ Bearbeiten' : (isExpanded ? '▲ Details' : '▼ Details')}
        </button>
        <button class="btn btn-ghost btn-sm" onclick="deleteMenuItem('${m.id}')" title="Löschen">✕</button>
      </div>
      ${isExpanded ? renderMenuDetails(m) : ''}
    </div>
  `;
}

// S153 — Rubrik-Dropdown (erst-klassig) statt Freitext: Rubrik + Unter-Rubrik aus
// menu_categories, inkl. inline „+ neu". Schreibt category_id/subcategory_id (DB-
// Trigger spiegelt die alten String-Spalten). Fallback bleibt das Legacy-Select.
function renderCategoryPicker(m) {
  const tree = buildCategoryTree(_categories);
  const KIND_LABEL = { menu: 'Menü', option: 'Optionen', extra: 'Extras' };
  const topByName = name => tree.find(t => t.name === name);
  const selTopId = m.categoryId || (topByName(m.category) || {}).id || '';
  const selTop = tree.find(t => t.id === selTopId) || null;
  const subByName = name => (selTop && (selTop.children || []).find(c => c.name === name)) || null;
  const selSubId = m.subcategoryId || (subByName(m.subcategory) || {}).id || '';

  const groups = ['menu', 'option', 'extra'].map(k => {
    const tops = tree.filter(t => t.kind === k);
    if (!tops.length) return '';
    return `<optgroup label="${KIND_LABEL[k]}">${tops.map(t =>
      `<option value="${htmlEscape(t.id)}" ${t.id === selTopId ? 'selected' : ''}>${t.icon ? htmlEscape(t.icon) + ' ' : ''}${htmlEscape(t.name)}</option>`).join('')}</optgroup>`;
  }).join('');

  const subOptions = (selTop ? (selTop.children || []) : []).map(c =>
    `<option value="${htmlEscape(c.id)}" ${c.id === selSubId ? 'selected' : ''}>${htmlEscape(c.name)}</option>`).join('');

  return `
    <div class="med-row">
      <label class="med-label">Rubrik</label>
      <div style="display:flex; gap:6px; align-items:center;">
        <select style="flex:1;" onchange="updateMenuCategoryId('${m.id}', this.value)">
          <option value="">— wählen —</option>
          ${groups}
        </select>
        <button class="btn btn-ghost btn-xs" title="neue Rubrik" onclick="menuAddCategoryInline('${m.id}','top')">+ neu</button>
      </div>
    </div>
    <div class="med-row">
      <label class="med-label">Unter-Rubrik</label>
      <div style="display:flex; gap:6px; align-items:center;">
        <select style="flex:1;" onchange="updateMenuSubcategoryId('${m.id}', this.value)" ${selTop ? '' : 'disabled'}>
          <option value="">— keine —</option>
          ${subOptions}
        </select>
        <button class="btn btn-ghost btn-xs" title="neue Unter-Rubrik" onclick="menuAddCategoryInline('${m.id}','sub')" ${selTop ? '' : 'disabled'}>+ neu</button>
      </div>
    </div>`;
}

function renderMenuDetails(m) {
  const tags = Array.isArray(m.dietaryTags) ? m.dietaryTags : [];
  const ings = Array.isArray(m.ingredients) ? m.ingredients : [];
  const meta = (m.meta && typeof m.meta === 'object') ? m.meta : {};
  return `
    <div class="menu-item-details">
      ${(categoriesFlagOn() && _categories.length) ? renderCategoryPicker(m) : `
      <div class="med-row">
        <label class="med-label">Kategorie</label>
        <select onchange="updateMenu('${m.id}','category', this.value)">
          ${CATEGORY_ORDER.map(c => `<option value="${c}" ${m.category === c ? 'selected' : ''}>${c}</option>`).join('')}
        </select>
      </div>`}
      <div class="med-row">
        <label class="med-label">Typ</label>
        <select onchange="updateMenu('${m.id}','itemType', this.value)">
          ${ITEM_TYPES.map(t => `<option value="${t.v}" ${m.itemType === t.v ? 'selected' : ''}>${t.label}</option>`).join('')}
        </select>
      </div>
      <div class="med-row">
        <label class="med-label">Einheit</label>
        <input type="text" value="${htmlEscape(m.unitLabel || '')}"
               placeholder="z.B. 0,4l / 9 St / 150g"
               onchange="updateMenu('${m.id}','unitLabel', this.value)">
      </div>
      <div class="med-row">
        <label class="med-label">Beschreibung</label>
        <textarea rows="2" placeholder="optional"
                  onchange="updateMenu('${m.id}','description', this.value)">${htmlEscape(m.description || '')}</textarea>
      </div>
      <div class="med-row">
        <label class="med-label">Diät-Tags</label>
        <div class="med-tag-list">
          ${DIET_TAGS.map(t => `
            <label class="med-tag-chip ${tags.includes(t) ? 'active' : ''}">
              <input type="checkbox" ${tags.includes(t) ? 'checked' : ''}
                     onchange="toggleMenuTag('${m.id}','${t}', this.checked)">
              ${t}
            </label>
          `).join('')}
        </div>
      </div>
      <div class="med-row">
        <label class="med-label">Zutaten <small>(Komma-getrennt — vom Gast einzeln streichbar)</small></label>
        <textarea rows="2" placeholder="z.B. cheddar, salat, tomate, zwiebeln, ketchup"
                  onchange="updateMenuIngredients('${m.id}', this.value)">${htmlEscape(ings.join(', '))}</textarea>
      </div>
      <div class="med-row">
        <label class="med-label">Meta (JSON) <small>(Power-User — Brötchen/Patty/Dressing-Konfiguration)</small></label>
        <textarea rows="3" class="me-meta"
                  placeholder='z.B. {"isBurger":true,"defaultPatty":"140g rindfleisch","allowPattySwap":true,"pattyGroup":"burger_patty","bunGroup":"burger_bun","extrasGroup":"burger_extras"}'
                  onchange="updateMenuMeta('${m.id}', this.value)">${htmlEscape(JSON.stringify(meta, null, 2))}</textarea>
      </div>
      <!-- S63 Block U.6 — Uber-Eats-Mapping-Feld -->
      <div class="med-row">
        <label class="med-label">Uber-Eats-Item-ID <small>(Mapping für eingehende Marketplace-Bestellungen — leer lassen wenn Gericht nicht bei Uber gelistet)</small></label>
        <input type="text" value="${htmlEscape(m.uberItemId || '')}"
               placeholder="z.B. eats_item_a1b2c3"
               onchange="updateMenu('${m.id}','uberItemId', this.value)"
               style="font-family: ui-monospace, monospace;">
      </div>
      ${(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb) ? `
      <!-- S151 ERP A1 — Rezept-Andockung + Nährwert-Badge (Flag ingredientsDb) -->
      <div class="med-row" style="border-top:1px solid var(--border); padding-top:10px; margin-top:6px;">
        <label class="med-label">Rezept & Nährwerte</label>
        <div style="display:flex; flex-direction:column; gap:6px; align-items:flex-start;">
          <div id="recipe-nutri-badge-${m.id}"><span class="hint" style="font-size:.8rem;">…</span></div>
          <button class="btn btn-secondary btn-sm" onclick="openRecipeEditor('${m.id}')">🥗 Rezept bearbeiten</button>
        </div>
      </div>` : ''}
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Aktionen
// -----------------------------------------------------------------------------
export async function addMenuItem() {
  // S155 — bei aktivem ingredientsDb öffnet „+ Gericht" den Konfigurator (ein
  // integrierter Erstellpfad statt des alten dünnen Formulars).
  if (categoriesFlagOn() && typeof window.openConfigurator === 'function') {
    window.openConfigurator(null);
    return;
  }
  // Phase 41 (S51): Defense-in-Depth — RLS würde Schreibversuche von
  // staff/host blocken, aber Client soll erst gar nicht versuchen.
  if (!requireEditPermission('menu', 'Gericht anlegen')) return;
  const state = getState();
  try {
    const newId = uid('m');
    await window.App.storage.menu.create({
      id: newId,
      name: 'Neues Gericht',
      category: 'Burger',
      subcategory: '',
      price: 10.00,
      available: true,
      displayOrder: ((state.menu || []).length + 1) * 10,
      itemType: 'main',
      ingredients: [],
      dietaryTags: [],
      meta: {},
      unitLabel: '',
      description: ''
    });
    _expandedIds.add(newId);
    renderMenuList();
    showToast('Gericht hinzugefügt — klick auf Details', 'success');
  } catch (e) {
    console.error('[addMenuItem]', e);
    showToast('Konnte nicht angelegt werden: ' + (e.message || e), 'error');
  }
}

export async function updateMenu(id, key, val) {
  // Phase 41 (S51): zentrales Schreib-Gate für ALLE Menu-Updates. Die
  // anderen Pfade (updateMenuIngredients/toggleMenuTag/updateMenuMeta)
  // delegieren auf diesen Eintrag — eine Stelle reicht.
  if (!requireEditPermission('menu', 'Gericht ändern')) return;
  try {
    await window.App.storage.menu.update(id, { [key]: val });
    showToast('Gespeichert', 'success');
  } catch (e) {
    console.error('[updateMenu]', e);
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// S153 — Rubrik per ID setzen. category_id führend; subcategory wird beim Rubrik-
// Wechsel zurückgesetzt (String mit, da Trigger leere IDs nicht anfasst).
export async function updateMenuCategoryId(id, catId) {
  if (!requireEditPermission('menu', 'Gericht ändern')) return;
  try {
    await window.App.storage.menu.update(id, { categoryId: catId || null, subcategoryId: null, subcategory: '' });
    showToast('Rubrik gesetzt', 'success');
    renderMenuList();
  } catch (e) { showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export async function updateMenuSubcategoryId(id, subId) {
  if (!requireEditPermission('menu', 'Gericht ändern')) return;
  try {
    const changes = subId ? { subcategoryId: subId } : { subcategoryId: null, subcategory: '' };
    await window.App.storage.menu.update(id, changes);
    showToast('Unter-Rubrik gesetzt', 'success');
    renderMenuList();
  } catch (e) { showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

// Inline „+ neu" aus dem Menü-Editor: legt eine Rubrik (top) bzw. Unter-Rubrik
// (sub, unter der aktuell gewählten Rubrik des Gerichts) an und ordnet sie zu.
export async function menuAddCategoryInline(id, level) {
  if (!requireEditPermission('menu', 'Rubrik anlegen')) return;
  const m = (getState().menu || []).find(x => x.id === id);
  if (!m) return;
  try {
    if (level === 'sub') {
      const tree = buildCategoryTree(_categories);
      const topId = m.categoryId || (tree.find(t => t.name === m.category) || {}).id;
      const top = tree.find(t => t.id === topId);
      if (!top) { showToast('Erst eine Rubrik wählen.', 'error'); return; }
      const name = ((await window.styledPrompt({ title: 'Neue Unter-Rubrik', label: `unter „${top.name}"`, placeholder: 'Name' })) || '').trim();
      if (!name) return;
      const sibs = top.children || [];
      const cat = await window.App.storage.menuCategories.create({ name, kind: top.kind, parentId: top.id, sortOrder: (sibs.length + 1) * 10, active: true });
      await ensureCategoriesLoaded();
      await window.App.storage.menu.update(id, { subcategoryId: cat.id });
    } else {
      const name = ((await window.styledPrompt({ title: 'Neue Rubrik', label: 'Name der Rubrik', placeholder: 'z. B. Burger' })) || '').trim();
      if (!name) return;
      const tops = buildCategoryTree(_categories).filter(t => t.kind === 'menu');
      const cat = await window.App.storage.menuCategories.create({ name, kind: 'menu', parentId: null, sortOrder: (tops.length + 1) * 10, active: true });
      await ensureCategoriesLoaded();
      await window.App.storage.menu.update(id, { categoryId: cat.id, subcategoryId: null, subcategory: '' });
    }
    showToast('Rubrik angelegt & zugeordnet', 'success');
    renderMenuList();
  } catch (e) { showToast('Anlegen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export async function updateMenuIngredients(id, csv) {
  const list = (csv || '')
    .split(',')
    .map(x => x.trim())
    .filter(Boolean);
  await updateMenu(id, 'ingredients', list);
}

export async function toggleMenuTag(id, tag, checked) {
  const state = getState();
  const m = (state.menu || []).find(x => x.id === id);
  if (!m) return;
  const current = Array.isArray(m.dietaryTags) ? m.dietaryTags.slice() : [];
  const idx = current.indexOf(tag);
  if (checked && idx < 0) current.push(tag);
  if (!checked && idx >= 0) current.splice(idx, 1);
  await updateMenu(id, 'dietaryTags', current);
  // Re-render der Detail-Box, damit Chip-Status passt.
  renderMenuList();
}

export async function updateMenuMeta(id, json) {
  let parsed;
  try {
    parsed = json && json.trim() ? JSON.parse(json) : {};
  } catch (e) {
    showToast('Meta-JSON ist ungültig: ' + e.message, 'error');
    return;
  }
  await updateMenu(id, 'meta', parsed);
}

// S155 — „Bearbeiten" öffnet bei aktivem Flag den Konfigurator (ein Pfad), sonst
// klappt es wie bisher die Inline-Details auf (Legacy-Fallback).
export function editMenuItem(id) {
  if (categoriesFlagOn() && typeof window.openConfigurator === 'function') {
    window.openConfigurator(id);
    return;
  }
  toggleMenuDetails(id);
}

export function toggleMenuDetails(id) {
  const willExpand = !_expandedIds.has(id);
  if (_expandedIds.has(id)) _expandedIds.delete(id);
  else _expandedIds.add(id);
  renderMenuList();
  // S151 ERP A1 — Nährwert-Badge des Gerichts laden, sobald die Details offen
  // sind (Flag-gated; Funktion ist No-Op ohne ingredientsDb).
  if (willExpand && typeof window.renderRecipeNutritionInto === 'function') {
    window.renderRecipeNutritionInto(id);
  }
}

export async function deleteMenuItem(id) {
  // Phase 41 (S51): Defense-in-Depth — Delete-Pfad nicht über updateMenu()
  // → eigener Gate-Check nötig.
  // S58 Block II (Phase 45): Soft-Delete statt Hard-Delete. Auslöser war
  // der SIMPLE-CHEESE-Restore-Vorfall in S57 — Hard-Delete war unwiderruflich
  // und Menu-Edits fehlten im Audit-Log. Jetzt: deleted_at-Spalte, Restore-
  // RPC vorhanden, Audit-Trail in audit_log.
  if (!requireEditPermission('menu', 'Gericht löschen')) return;
  const _del = await window.styledConfirm({
    title: 'Gericht löschen?', danger: true, confirmLabel: 'Löschen',
    body: 'Kein Hard-Delete — der Eintrag bleibt wiederherstellbar (Bereich „🗑️ Gelöschte Gerichte" unten).',
    reasonField: true
  });
  if (!_del.confirmed) return;
  const reason = _del.reason || '';
  try {
    await window.App.storage.menu.softDelete(id, reason);
    _expandedIds.delete(id);
    renderMenuList();
    await renderDeletedMenuList();
    showToast('Gericht gelöscht (wiederherstellbar)', 'success');
  } catch (e) {
    console.error('[deleteMenuItem]', e);
    showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/**
 * S58 Block II — Restore-View für soft-gelöschte Menu-Items.
 * Lädt deleted_at IS NOT NULL aus der DB (nicht im aktiven _state.menu) und
 * rendert sie als kollabierbare Liste am Ende des Menü-Editors.
 */
export async function renderDeletedMenuList() {
  const wrap = document.getElementById('menu-deleted-list');
  if (!wrap) return;
  let items = [];
  try {
    items = await window.App.storage.menu.listDeleted();
  } catch (e) {
    console.warn('[menu] listDeleted:', e);
  }
  if (!items.length) {
    wrap.innerHTML = `
      <div class="hint" style="padding:12px; text-align:center;">
        Keine gelöschten Gerichte. (Hier landen Items nach „✕ Löschen".)
      </div>`;
    return;
  }
  wrap.innerHTML = `
    <div style="font-size:.85rem; color:var(--ink-soft); margin-bottom:10px;">
      ${items.length} Eintrag/Einträge — Wiederherstellung möglich, solange das
      Item nicht final aus der DB entfernt wurde.
    </div>
    <div style="display:flex; flex-direction:column; gap:8px;">
    ${items.map(m => `
      <div data-deleted-item="${htmlEscape(m.id)}"
           style="display:grid; grid-template-columns:1fr auto; gap:12px; align-items:center;
                  padding:10px 14px; background:var(--bg-soft);
                  border:1px solid var(--border); border-radius:8px;">
        <div style="min-width:0;">
          <div style="font-weight:600; line-height:1.2;">${htmlEscape(m.name || '—')}</div>
          <div style="color:var(--ink-soft); font-size:.82rem; margin-top:2px;">
            ${htmlEscape(m.category || '—')} · ${(m.price ?? 0).toFixed(2).replace('.', ',')} €
            · gelöscht ${m.deletedAt ? new Date(m.deletedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
          </div>
          ${m.deletedReason ? `
            <div style="color:var(--ink-soft); font-size:.78rem; margin-top:2px; font-style:italic;">
              Grund: ${htmlEscape(m.deletedReason)}
            </div>` : ''}
        </div>
        <button class="btn btn-primary btn-sm" style="white-space:nowrap;"
                onclick="restoreMenuItem('${m.id}')">
          ↺ Wiederherstellen
        </button>
      </div>
    `).join('')}
    </div>
  `;
}

export async function restoreMenuItem(id) {
  if (!requireEditPermission('menu', 'Gericht wiederherstellen')) return;
  try {
    await window.App.storage.menu.restore(id);
    renderMenuList();
    await renderDeletedMenuList();
    showToast('Gericht wiederhergestellt', 'success');
  } catch (e) {
    console.error('[restoreMenuItem]', e);
    showToast('Wiederherstellen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderMenuEditor,
    // S188-U — Speisekarte-Hub
    menuHubShow,
    menuHubSetView,
    renderMenuList,
    toggleMenuCat,   // S217 Cut 1 — Rubrik-Gruppe auf-/zuklappen
    addMenuItem,
    updateMenu,
    updateMenuCategoryId,
    updateMenuSubcategoryId,
    menuAddCategoryInline,
    updateMenuIngredients,
    toggleMenuTag,
    updateMenuMeta,
    toggleMenuDetails,
    editMenuItem,
    deleteMenuItem,
    // S58 Block II
    restoreMenuItem,
    renderDeletedMenuList
  });
}
